<template>
  <div>
    <div v-if="!questionAnswered">
      <div class="mt-6 py-5 text-left">
        <!-- <p class="text-sm mb-2">Frage {{ question.prio }} von 6</p> -->
        <h1 class="text-lg font-extralight">
          {{ generatedQuestion }}
        </h1>
        <p v-if="question.sub_question" class="mt-3 text-sm font-extralight">
          {{ question.sub_question }}
        </p>

        <div class="border-t border-gray-400 mt-4"></div>

        <p v-if="isMultipleChoice" class="mt-4 text-xs font-extralight">
          Mehrere Antworten möglich
        </p>

        <p v-if="!isMultipleChoice" class="mt-4 text-xs font-extralight">
          Eine Antwort möglich
        </p>
      </div>

      <form @submit.prevent="submit" novalidate class="mt-3 text-left">
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul>
            <li v-for="(choice, idx) in choices" :key="idx" @click="select(choice)" class="
    cursor-pointer
    block
    focus:outline-none
    transition
    duration-150
    ease-in-out
  " :class="customClass(choice, idx)">
              <div class="flex items-center px-4 py-4">
                <div class="min-w-0 flex-1 flex items-center">
                  <!-- semantic input: radio vs checkbox -->
                  <input :type="isMultipleChoice ? 'checkbox' : 'radio'" class="hidden" :name="'choice_' + question.id"
                    :value="choice.value" :checked="choice.selected" @change.stop />

                  <!-- icon: same SVG, just color based on selection -->
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                    stroke="currentColor" class="w-5 h-5 mr-5"
                    :class="choice.selected ? 'text-white' : 'text-gray-200'">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>

                  <!-- normal choice text OR "other" with textarea -->
                  <div class="flex-1">
                    <div class="text-sm leading-5 font-medium"
                      :class="choice.selected ? 'text-white' : 'text-gray-600'">
                      {{ choice.value }}
                    </div>

                    <!-- if this is the "other" option, show textarea -->
                    <div v-if="choice.isOther" class="mt-2">
                      <textarea v-model="choice.text" @click.stop @input="updateOtherText(choice)" rows="3"
                        class="shadow-sm focus:ring-markencode-blue focus:border-markencode-blue mt-1 block w-full border border-gray-300 rounded-md"></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </li>

          </ul>
        </div>

        <submit-button :sending="sending" :validated="validated" class="max-w-4xl ml-auto mr-auto mt-4">{{
          translations.save }}</submit-button>
      </form>
    </div>

    <div v-if="questionAnswered && session.show_live_results" class="mt-6 px-4 py-5 text-center">
      <h1 class="text-3xl sm:text-4xl font-extralight">{{ translations.thank_you }}</h1>
      <p class="text-xl font-extralight">{{ translations.lets_go_on }}</p>

      <pie-chart v-if="!isMultipleChoice" :session="session" :translations="translations"></pie-chart>

      <bar-chart v-if="isMultipleChoice" :session="session" :translations="translations"></bar-chart>
    </div>

    <div v-if="questionAnswered && !session.show_live_results" class="mt-6 px-8 py-5 text-left">
      <h1 class="text-3xl sm:text-4xl font-extralight mb-3">
        {{ translations.thank_you }}
      </h1>
    </div>
  </div>
</template>

<script>
import SubmitButton from '@/Shared/SubmitButton';
import PieChart from '@/Pages/LiveResults/PieChart';
import BarChart from '@/Pages/LiveResults/BarChart';
import { throttle } from 'lodash';

export default {
  components: {
    SubmitButton,
    PieChart,
    BarChart,
  },

  props: {
    translations: {},
    participant: {
      required: true,
    },
    question: {
      required: true,
    },
    session: {
      required: true,
      type: Object,
    },
    answerParameters: {},
  },

  data() {
    return {
      sending: false,
      answered: false,
      choices: [],
      form: {
        choices: [],
      },
    };
  },

  created() {
    const parameters = this.question.parameters;
    const opts = parameters.options || {};

    const hasOther =
      opts.has_other === 1 ||
      opts.has_other === '1';

    // base choices
    this.choices = parameters.choices.map((choice) => {
      return {
        value: choice,
        selected: false,
        isOther: false,
        text: '',
      };
    });

    // optional "other" choice at the end
    if (hasOther) {
      this.choices.push({
        value: opts.other_label || 'Sonstiges',
        selected: false,
        isOther: true,
        text: '',
      });
    }
  },


  methods: {
    select(selectedChoice) {
      if (!this.isMultipleChoice) {
        // single choice (radio)
        this.choices = this.choices.map((choice) => {
          return {
            ...choice,
            selected: choice.value === selectedChoice.value,
          };
        });
      } else {
        // multiple choice (checkbox)
        selectedChoice.selected = !selectedChoice.selected;

        this.choices = this.choices.map((choice) => {
          if (choice.value === selectedChoice.value) {
            return {
              ...choice,
              selected: selectedChoice.selected,
            };
          }
          return choice;
        });
      }

      this.rebuildFormChoices();
    },


    rebuildFormChoices() {
      this.form.choices = this.choices
        .filter(c => c.selected)
        .map(c => {
          if (c.isOther) {
            // Use the typed text if available, otherwise the label
            return (c.text && c.text.trim().length)
              ? c.text.trim()
              : c.value;
          }
          return c.value;
        });
    },


    updateOtherText(choice) {
      if (!choice.isOther) return;

      if (!choice.selected) {
        if (!this.isMultipleChoice) {
          // In single mode, "other" becomes the only selected
          this.choices = this.choices.map(c => ({
            ...c,
            selected: c === choice,
          }));
        } else {
          // In multiple mode, just select it
          choice.selected = true;
        }
      }

      this.rebuildFormChoices();
    },


    submit() {
      this.sending = true;

      const payload = {
        choices: this.form.choices,
      };

      window.axios
        .post(
          this.route('live.questions.store', {
            uuid: this.participant.uuid,
            questionId: this.question.id,
          }),
          payload,
        )
        .then(() => {
          this.sending = false;
          this.answered = true;
        })
        .catch((error) => {
          this.sending = false;
        });
    },

    customClass(choice, idx) {
      let classes = '';

      if (idx) {
        classes += 'border-t border-gray-200 ';
      }

      if (choice.selected) {
        classes +=
          'bg-markencode-blue hover:bg-markencode-blue focus:bg-markencode-blue';
      } else {
        classes += 'bg-white hover:bg-gray-50 focus:bg-gray-50';
      }

      return classes;
    },

  },

  computed: {
    generatedQuestion() {

      if (!this.question?.brandname && !this.session.brandname && this.question?.question_default) {
        return this.question?.question_default;
      }

      if (this.question?.is_placeholder_question && (this.question?.brandname || this.session.brandname)) {
        return this.question.question.replace('%BRANDNAME', this.question?.brandname || this.session.brandname);
      }

      return this.question.question;
    },

    // selected() {
    //   if (this.choices.length) {
    //     return this.choices.filter((choice) => choice.selected).length;
    //   }

    //   return 0;
    // },

    isMultipleChoice() {
      const ma = this.question.parameters.options.multiple_answers;
      return ma === true || ma === 1 || ma === '1';
    },

    validated() {
      return Array.isArray(this.form.choices) && this.form.choices.length > 0;
    },

    questionAnswered() {
      // return (
      //   (this.answerParameters && this.answerParameters.choices.length > 0) ||
      //   this.answered
      // );

      if (this.answered) {
        return true;
      }

      if (!this.answerParameters) {
        return false;
      }

      return Object.keys(this.answerParameters).length > 0;

      // return false;
    },

    // channel() {
    //   return Echo.channel(
    //     'markencode-productivity.session.' + this.participant.markencode_session.id
    //   );
    // },
  },


};
</script>